import { fromBase64, toBase64 } from '../../core/bytes/encoding';
import { classifyFetchFailure, TransportError } from '../../core/transport/errors';
import { fetchResultFrom, type Fetcher, type FetchInit, type FetchResult } from '../../core/transport/fetcher';
import { hostFetchers } from './transport';

/**
 * Bridge to the desktop shell (desktop/shell, a small C++ app around the OS webview). The shell
 * injects `window.__merklens` plus a handful of `__merklens_*` functions before any page script runs;
 * each returns a Promise (rejections carry a ShellError object, not an Error). Nothing here is used
 * by the web build, where `isDesktop()` is false.
 */
export interface ShellInfo {
  version: string;
  platform: 'macos' | 'windows' | 'linux';
  /** Port of the shell's loopback server that serves this page. */
  port: number;
}

export interface ShellFetchRequest {
  id: string;
  url: string;
  method: 'GET' | 'POST';
  headers: Record<string, string>;
  body?: string;
  bodyEncoding?: 'utf8' | 'base64';
}

export interface ShellFetchResponse {
  status: number;
  /** Effective URL after redirects. */
  url: string;
  headers: Record<string, string>;
  /** base64 */
  body: string;
}

declare global {
  interface Window {
    __merklens?: ShellInfo;
    __merklens_fetch?: (req: ShellFetchRequest) => Promise<ShellFetchResponse>;
    __merklens_cancel?: (id: string) => Promise<boolean>;
    __merklens_peer_chain?: (host: string, port: number | null) => Promise<string[]>;
    __merklens_system_roots?: () => Promise<string[]>;
    __merklens_open?: (url: string) => Promise<void>;
    __merklens_save?: (file: { name: string; mime: string; body: string }) => Promise<boolean>;
  }
}

/** True inside the desktop shell. */
export function isDesktop(): boolean {
  return typeof window !== 'undefined' && window.__merklens !== undefined;
}

/** Identifies the desktop app to logs the way well-behaved clients do: name, version, where to find out more. */
export function userAgent(): string {
  return `merklens/${__APP_VERSION__} (+https://merklens.app)`;
}

let seq = 0;

/** The shell's HTTP client: no CORS, our User-Agent, cancellable through the request id. */
async function shellFetch(url: string, init?: FetchInit): Promise<FetchResult> {
  const id = `${Date.now().toString(36)}-${++seq}`;
  const req: ShellFetchRequest = { id, url, method: init?.method ?? 'GET', headers: { ...(init?.headers ?? {}), 'User-Agent': userAgent() } };
  if (init?.body !== undefined) {
    if (typeof init.body === 'string') {
      req.body = init.body;
      req.bodyEncoding = 'utf8';
    } else {
      req.body = toBase64(init.body);
      req.bodyEncoding = 'base64';
    }
  }
  const signal = init?.signal;
  if (signal?.aborted) throw new TransportError('aborted', 'request aborted', { url, via: 'desktop' });
  const onAbort = () => void window.__merklens_cancel?.(id);
  signal?.addEventListener('abort', onAbort);
  try {
    const res = await window.__merklens_fetch!(req);
    return await fetchResultFrom(res.url || url, res.status, res.headers, fromBase64(res.body), 'desktop');
  } catch (e) {
    throw classifyFetchFailure(e, url, 'desktop');
  } finally {
    signal?.removeEventListener('abort', onAbort);
  }
}

/** Cross-origin links open in the user's browser; the shell window stays on the app. */
function openExternalLinks(ev: MouseEvent): void {
  if (ev.defaultPrevented || ev.button !== 0) return;
  const a = (ev.target as Element | null)?.closest?.('a[href]');
  if (!(a instanceof HTMLAnchorElement)) return;
  let target: URL;
  try {
    target = new URL(a.href, location.href);
  } catch {
    return;
  }
  if ((target.protocol !== 'http:' && target.protocol !== 'https:') || target.origin === location.origin) return;
  ev.preventDefault();
  void window.__merklens_open?.(target.href);
}

/** Wires the shell in: all log traffic goes through its HTTP client, external links open outside. */
export function setupDesktop(): void {
  if (!isDesktop()) return;
  hostFetchers.value = [{ kind: 'desktop', fetch: shellFetch } satisfies Fetcher];
  document.addEventListener('click', openExternalLinks);
}

/** Asks the shell to save a file (native save dialog). Resolves false when the user cancels. */
export function saveFile(name: string, data: Uint8Array | string, mime: string): Promise<boolean> {
  const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data;
  return window.__merklens_save ? window.__merklens_save({ name, mime, body: toBase64(bytes) }) : Promise.resolve(false);
}

let rootsPromise: Promise<Uint8Array[]> | undefined;
/** The operating system's trusted root certificates (DER), fetched once per session. */
export function systemRoots(): Promise<Uint8Array[]> {
  rootsPromise ??= window.__merklens_system_roots!().then((b64s) => b64s.map((b) => fromBase64(b)));
  return rootsPromise;
}

/** A server's certificate chain, read straight from a TLS handshake (leaf first). */
export async function peerChain(host: string, port?: number): Promise<Uint8Array[]> {
  try {
    const b64s = await window.__merklens_peer_chain!(host, port ?? null);
    return b64s.map((b) => fromBase64(b));
  } catch (e) {
    throw e instanceof Error ? e : new Error(typeof e === 'object' && e !== null && 'message' in e ? String((e as { message: unknown }).message) : String(e));
  }
}
